import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Projects from './Projects';
import Experience from './Experience';
import Funhouse from './Funhouse';
import { Mail, Download } from 'lucide-react';

interface AboutProps {
  activeSection: string;
  onOpenPalette: () => void;
  setActiveSection: (section: string) => void;
  onTriggerEffect: (effectId: string, origin?: { x: number; y: number }) => void;
  lensEnabled: boolean;
  snitchActive: boolean;
}

const About: React.FC<AboutProps> = ({
  activeSection,
  onOpenPalette,
  setActiveSection,
  onTriggerEffect,
  lensEnabled,
  snitchActive,
}) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Scroll to top when component mounts
    const mainContent = document.querySelector('main');
    if (mainContent && window.innerWidth >= 1024) {
      mainContent.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }

    // Then handle section scrolling only if there's a scrollTo state
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const sectionId = location.state.scrollTo;
        const element = document.getElementById(sectionId);
        if (element) {
          if (mainContent && window.innerWidth >= 1024) {
            if (sectionId === 'projects') {
              mainContent.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              // Desktop: scroll the main content container
              const mainRect = mainContent.getBoundingClientRect();
              const targetElement = element.querySelector('h2') || element;
              const targetRect = targetElement.getBoundingClientRect();
              const offsetWithinMain = targetRect.top - mainRect.top;
              const targetTop = Math.max(mainContent.scrollTop + offsetWithinMain, 0);
              mainContent.scrollTo({ top: targetTop, behavior: 'smooth' });
            }
          } else {
            // Mobile: scroll the window
            const yOffset = -80;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }
        // Clear the location state after scrolling
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [location]);

  return (
    <div className="animate-fade-in">
      {/* Profile and About for Mobile - Always visible on mobile */}
      {(activeSection === 'all' || isMobile) && (
        <div className="lg:hidden mb-8 md:mb-12">
          <div className="flex flex-col items-center py-6 md:py-8 mb-6 md:mb-8">
            <img 
              src="/images/profile-photo.webp"
              alt="Parth Kohale" 
              className="w-20 h-20 md:w-24 md:h-24 rounded-full border-2 border-dev-accent shadow-glow mb-3 md:mb-4 object-cover"
              loading="eager"
            />
            <h1 className="text-2xl md:text-3xl font-bold font-mono mb-1.5 md:mb-2">Parth Kohale</h1>
            <h2 className="text-dev-text-muted text-base md:text-lg font-mono mb-3 md:mb-4">AI Engineer @ Deriv </h2>
            
            {/* Terminal-style path for mobile — doubles as the command palette trigger */}
            <button
              onClick={onOpenPalette}
              aria-label="Open command palette"
              className="w-full max-w-md flex items-center text-left font-mono text-sm md:text-base p-4 md:p-4 bg-dev-bg-light rounded-lg border border-dev-border active:border-dev-accent/60 transition-colors"
            >
              <span className="text-dev-accent-cyan mr-2">&gt;</span>
              <span className="font-bold text-black dark:text-white">
                cd ~/portfolio
              </span>
              <span className="text-dev-text terminal-cursor">█</span>
            </button>
          </div>
          
          <div className="dev-card p-4 md:p-6 mb-8 md:mb-12">
            <h3 className="text-xl md:text-2xl font-bold font-mono text-dev-text mb-3 md:mb-4">
              <span className="text-dev-accent">//</span> About Me
            </h3>
            <div className="space-y-3 text-dev-text-muted text-sm leading-relaxed mb-4">
              <p className="font-mono">
                <span className="text-dev-accent-cyan">const</span> <span className="text-dev-text">developer</span> = &#123;
              </p>
              <p className="pl-4 font-mono">
                <span className="text-dev-text-dimmed">name:</span> <span className="text-dev-success">"Parth Kohale"</span>,
              </p>
              <p className="pl-4 font-mono">
                <span className="text-dev-text-dimmed">education:</span> <span className="text-dev-success">"CSE @BITS Pilani"</span>,
              </p>
              <p className="pl-4 font-mono">
                <span className="text-dev-text-dimmed">location:</span> <span className="text-dev-success">"Dubai, UAE"</span>,
              </p>
              <p className="pl-4 font-mono">
                <span className="text-dev-text-dimmed">interests:</span> [<span className="text-dev-success">"AI/ML"</span>, <span className="text-dev-success">"GenAI"</span>, <span className="text-dev-success">"Data Science"</span>, <span className="text-dev-success">"Astrophysics"</span>, <span className="text-dev-success">"Guitar"</span>]
              </p>
              <p className="font-mono">&#125;;</p>
            </div>
            
            <div className="border-t border-dev-border pt-4 space-y-2 text-sm">
              <a 
                href="/PARTH_KOHALE_CV.pdf" 
                download
                className="flex items-center gap-2 text-dev-text-muted hover:text-dev-text transition-colors"
              >
                <Download size={16} className="text-dev-accent" />
                <span>Download Resume</span>
              </a>
            </div>
          </div>
        </div>
      )}
      
      {/* Projects Section - Always visible on mobile when activeSection is 'all', conditional on desktop.
          `hidden` when neither 'all' nor this section's own id is active — on
          desktop, an inactive section otherwise still renders its (empty)
          <section> box with its own bottom margin, leaving a visible gap
          before whatever section actually has content (e.g. Funhouse, at
          the end). Mobile is unaffected: it always passes activeSection
          'all', which never takes this branch. display:none also collapses
          the section out of getClientRects() entirely, which is exactly
          what useSectionNavigation's findVisibleSection and Funhouse's
          getProjectsScrollContainer rely on to skip straight to whichever
          section (and <main>) is actually visible right now. */}
      <section
        id="projects"
        className={`scroll-mt-20 lg:scroll-mt-0 mb-12 lg:mb-16 ${
          activeSection === 'all' || activeSection === 'projects' ? '' : 'hidden'
        }`}
      >
        {(activeSection === 'all' || activeSection === 'projects') && (
          // Projects renders its own heading (on the same line as the
          // carousel controls, see Projects.tsx) rather than About
          // duplicating it externally.
          <Projects />
        )}
      </section>

      {/* Experience Section - Always visible on mobile when activeSection is 'all', conditional on desktop */}
      <section
        id="experience"
        className={`scroll-mt-20 mb-12 lg:mb-16 ${
          activeSection === 'all' || activeSection === 'experience' ? '' : 'hidden'
        }`}
      >
        {(activeSection === 'all' || activeSection === 'experience') && (
          <>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-mono mb-6 lg:mb-8 text-dev-text">
              <span className="text-dev-accent">//</span> Experience
            </h2>
            <Experience />
          </>
        )}
      </section>

      {/* Contact Section - Always visible on mobile when activeSection is 'all', conditional on desktop */}
      <section
        id="contact"
        className={`scroll-mt-20 mb-12 lg:mb-16 ${
          activeSection === 'all' || activeSection === 'contact' ? '' : 'hidden'
        }`}
      >
        {(activeSection === 'all' || activeSection === 'contact') && (
          <>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-mono mb-6 lg:mb-8 text-dev-text">
              <span className="text-dev-accent">//</span> Get In Touch
            </h2>
            <div className="dev-card p-5 md:p-6 lg:p-8">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="p-2 md:p-3 bg-dev-bg-light rounded-lg border border-dev-border">
                  <Mail className="text-dev-accent" size={20} />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold font-mono mb-1.5 md:mb-2 text-dev-text">Email</h3>
                  <p className="text-sm md:text-base text-dev-text-muted mb-2 md:mb-3">
                    Feel free to reach out for collaborations or opportunities
                  </p>
                  <a 
                    href="mailto:parthkohale@gmail.com"
                    className="font-mono text-dev-accent hover:text-dev-accent-bright transition-colors inline-flex items-center gap-2 group"
                  >
                    parthkohale@gmail.com
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </section>

      {/* Funhouse Section - owns its own heading/section wrapper (unlike the
          sections above, which About renders around a headless child), see
          Funhouse.tsx. Always visible on mobile when activeSection is 'all',
          conditional on desktop. */}
      {(activeSection === 'all' || activeSection === 'funhouse') && (
        <Funhouse
          setActiveSection={setActiveSection}
          onTriggerEffect={onTriggerEffect}
          lensEnabled={lensEnabled}
          snitchActive={snitchActive}
        />
      )}
    </div>
  );
};

export default About;